<template>
  <div class="brand-selection">
    <div class="brand-selection__content">
      <div class="brand-selection__header">
        <div class="brand-selection__logo">
          <img
            src="@/assets/images/pvh-logo.png"
            alt="PVH"
          >
        </div>

        <div class="brand-selection__divider" />

        <h1 class="brand-selection__title">
          Welcome to <span>Hub3D</span>
        </h1>

        <p class="brand-selection__subtitle">
          PLEASE SELECT A BRAND TO CONTINUE
        </p>
      </div>

      <div class="brand-selection__brands">
        <button
          v-for="brand in availableBrands"
          :key="brand.id"
          type="button"
          class="brand-card"
          :disabled="isSelectingBrand"
          @click="selectBrand(brand)"
        >
          <div
            class="brand-card__visual"
            :style="getBrandImageStyle(brand)"
          >
            <div class="brand-card__overlay" />
          </div>

          <div class="brand-card__footer">
            <div>
              <div class="brand-card__hint">
                Enter the {{ brand.name }} workspace
              </div>
            </div>
            <span class="brand-card__arrow"> → </span>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ckImage from '@/assets/images/ck.png'
import thImage from '@/assets/images/th.png'

export default {
  name: 'BrandSelection',

  data () {
    return {
      isSelectingBrand: false,
      brandImages: {
        CK: ckImage,
        TH: thImage
      }
    }
  },

  computed: {
    ...mapGetters({
      getAvailableBrands: 'getAvailableBrands'
    }),

    /**
     * Available brands for the authenticated user.
     *
     * @returns {object[]}
     */
    availableBrands () {
      return this.getAvailableBrands || []
    }
  },

  async created () {
    if (!this.availableBrands.length) {
      await this.fetchCognitoUserData()
    }
  },

  methods: {
    ...mapActions({
      selectBrandForUser: 'selectBrand',
      fetchCognitoUserData: 'fetchCognitoUserData'
    }),

    /**
     * Returns the background image for a brand.
     *
     * @param   {object} brand
     *
     * @returns {object}
     */
    getBrandImageStyle (brand) {
      const image = this.brandImages[brand.code]

      return image
        ? {
            backgroundImage: `url(${image})`
          }
        : {}
    },

    /**
     * Selects a brand and redirects the user to the Hub.
     *
     * @param   {object}        brand
     *
     * @returns {Promise<void>}
     */
    async selectBrand (brand) {
      if (this.isSelectingBrand) {
        return
      }

      this.isSelectingBrand = true

      try {
        await this.selectBrandForUser(brand.id)

        await this.$router.push({
          name: 'LibraryStylesTypes'
        })
      } finally {
        this.isSelectingBrand = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$divider-height: 1px;
$brand-name-z-index: 1;
$brand-image-scale: 1.04;

.brand-selection {
  width: percentage(100);
  height: 100vh;
  overflow: auto;
  color: $white;
  background: $black;
}

.brand-selection__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  width: percentage(85);
  max-width: spacing(150);
  min-height: percentage(100);
  margin: 0 auto;
  padding: spacing(8) 0;
}

.brand-selection__header {
  margin-bottom: spacing(7);
  text-align: center;
}

.brand-selection__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: spacing(2);

  img {
    display: block;
    width: spacing(12);
    max-width: percentage(100);
    height: auto;
  }
}

.brand-selection__divider {
  width: percentage(8);
  min-width: spacing(8);
  height: $divider-height;
  margin: 0 auto spacing(5);
  background: rgba($white, 0.6);
}

.brand-selection__title {
  margin: 0;
  font-weight: $font-weight-regular;
  font-size: spacing(8);
  line-height: 1;
  letter-spacing: spacing(-0.1);

  span {
    color: rgba($white, 0.8);
  }
}

.brand-selection__subtitle {
  margin: spacing(4) 0 0;
  color: rgba($white, 0.7);
  font-weight: $font-weight-medium;
  font-size: spacing(1.75);
  letter-spacing: spacing(0.25);
}

.brand-selection__brands {
  display: flex;
  gap: spacing(5);
  width: percentage(100);
}

.brand-card {
  display: flex;
  flex: 1 1 percentage(50);
  flex-direction: column;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  color: $white;
  text-align: left;
  background: #10151b;
  border-radius: spacing(1.5);
  outline: none;
  box-shadow: $shadow-default;
  cursor: pointer;
  appearance: none;
}

.brand-card__visual {
  position: relative;
  width: percentage(100);
  overflow: hidden;
  background: none;
  aspect-ratio: 4 / 3;
}

.brand-card__visual::before {
  position: absolute;
  background-image: inherit;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transform: scale(1);
  transition: transform 1.2s ease-in-out;
  content: '';
  inset: 0;
}

.brand-card__overlay {
  position: absolute;
  z-index: $brand-name-z-index;
  inset: 0;
  background:
    linear-gradient(
      to bottom,
      transparent 60%,
      rgba($black, 0.25) 100%
    );
}

.brand-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: spacing(3) spacing(3.5);
  color: $white;
  background: #10151b;
}

.brand-card__action {
  margin-bottom: spacing(1);
  font-weight: $font-weight-medium;
  font-size: spacing(1.75);
  letter-spacing: spacing(0.1);
}

.brand-card__hint {
  color: rgba($white, 0.65);
  font-size: spacing(1.5);
}

.brand-card__arrow {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: spacing(5);
  height: spacing(5);
  margin-left: spacing(3);
  color: $white;
  font-size: spacing(2.5);
  border: 1px solid rgba($white, 0.7);
  border-radius: percentage(50);
  transition: transform 0.35s ease, background 0.35s ease;
}

.brand-card:hover {
  .brand-card__visual::before {
    transform: scale($brand-image-scale);
  }

  .brand-card__arrow {
    background: rgba($white, 0.08);
    transform: translateX(spacing(0.5));
  }
}

.brand-card:disabled {
  cursor: default;
  opacity: 0.7;
}

.brand-card:disabled .brand-card__visual::before {
  transform: scale(1);
}

.brand-card:disabled .brand-card__arrow {
  transform: none;
}

@media (max-width: $media-query-s) {
  .brand-selection__content {
    width: percentage(90);
    padding: spacing(5) 0;
  }

  .brand-selection__title {
    font-size: spacing(6);
  }

  .brand-selection__brands {
    flex-direction: column;
  }

  .brand-card {
    flex: 1 1 auto;
  }

  .brand-card__visual {
    aspect-ratio: 16 / 9;
  }
}
</style>
